import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { LoadingButton } from "./LoadingSkeleton";

// Save the week you are currently viewing under a name.
// Picking an existing saved week switches the modal into overwrite mode, the same
// way the macro calculator's preset save modal pre-fills and re-targets.
const SaveWeekModal = ({
  show,
  onHide,
  onConfirm,
  savedWeeks = [],
  recipeCount = 0,
  isSaving = false,
  currentPlanType = "current",
  errorMessage = null,
  onClearError,
}) => {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [overwriteId, setOverwriteId] = useState("");

  // Reset each time the modal opens so a previous attempt never leaks through.
  useEffect(() => {
    if (show) {
      setName("");
      setNotes("");
      setOverwriteId("");
    }
  }, [show]);

  const handleOverwriteChange = (e) => {
    const id = e.target.value;
    setOverwriteId(id);
    if (onClearError) onClearError();

    if (!id) {
      setName("");
      setNotes("");
      return;
    }
    const existing = savedWeeks.find((w) => String(w.id) === String(id));
    if (existing) {
      setName(existing.name);
      setNotes(existing.notes || "");
    }
  };

  const trimmedName = name.trim();
  const canSave = trimmedName.length > 0 && recipeCount > 0 && !isSaving;

  const handleConfirm = () => {
    if (!canSave) return;
    onConfirm({
      name: trimmedName,
      notes: notes.trim(),
      overwriteId: overwriteId || null,
    });
  };

  const weekLabel = currentPlanType === "next" ? "next week" : "this week";

  return (
    <Modal
      show={show}
      onHide={isSaving ? undefined : onHide}
      centered
      backdrop="static"
      keyboard={!isSaving}
    >
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>
          <i className="fas fa-bookmark me-2"></i>
          Save This Week
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {recipeCount === 0 ? (
          <div className="alert alert-warning mb-0">
            <i className="fas fa-exclamation-triangle me-2"></i>
            There are no recipes in {weekLabel} to save yet.
          </div>
        ) : (
          <>
            <p>
              Save all <strong>{recipeCount} recipes</strong> from {weekLabel} so you
              can load the whole plan back later.
            </p>

            {savedWeeks.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Overwrite an existing saved week (optional)</Form.Label>
                <Form.Select
                  value={overwriteId}
                  onChange={handleOverwriteChange}
                  disabled={isSaving}
                >
                  <option value="">Save as a new week…</option>
                  {savedWeeks.map((week) => (
                    <option key={week.id} value={week.id}>
                      {week.name} ({week.recipe_count} recipes)
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={name}
                maxLength={255}
                placeholder="e.g. Easy Winter Week"
                onChange={(e) => {
                  setName(e.target.value);
                  if (onClearError) onClearError();
                }}
                disabled={isSaving}
                isInvalid={Boolean(errorMessage)}
                autoFocus
              />
              {errorMessage && (
                <Form.Control.Feedback type="invalid">
                  {errorMessage}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group>
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notes}
                placeholder="Anything worth remembering about this week…"
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSaving}
              />
            </Form.Group>

            {overwriteId && (
              <div className="alert alert-warning mt-3 mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>
                This will replace the recipes stored in that saved week.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton
          variant="primary"
          onClick={handleConfirm}
          disabled={!canSave}
          isLoading={isSaving}
          loadingText="Saving..."
        >
          <i className="fas fa-bookmark me-1"></i>
          {overwriteId ? "Overwrite" : "Save Week"}
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveWeekModal;
